<template>
    <div class="upsert-expense">
        <transition name="dialogfade">
            <k-dialog v-if="showDialog" class="default__dialog upsert-expense-dialog">
                <div class="icon__container">
                    <i class="fa-sharp fa-solid fa-money-bill-wave"></i>
                </div>
                <div class="header__container">
                    <svg viewBox="0 0 300 50" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" class="icon__top-bar" clip-rule="evenodd" d="M10 0C4.47715 0 0 4.47715 0 10V50H300V10C300 4.47715 295.523 0 290 0H184.388C184.388 9.01736 180.732 17.6654 174.226 24.0416C167.72 30.4179 158.895 34 149.694 34C140.492 34 131.668 30.4179 125.161 24.0416C118.655 17.6654 115 9.01737 115 5.13386e-06L149.694 0H10Z" fill="#D4B139"></path></svg>
                </div>
                <div class="create-document__header">
                    <h1 class="dialog--title">{{ editMode ? 'Edit Record' : 'Create Record' }}</h1>
                </div>
                <div class="upsert-item__container">
                    <section class="upsert-item__section">
                        <p class="field--required">*Required</p>
                        <div class="input__row">
                            <label class="input__label">
                                *Description
                                <k-input v-model="expense.description" :class="[{ 'error': hasExpenseDescriptionError }]" @keydown="hasExpenseDescriptionError = false" />
                                <span class="item-input--vuelidate-error" v-if="hasExpenseDescriptionError"><i>Description is required</i></span>
                            </label>
                        </div>
                        <div class="input__row pair">
                            <label class="input__label">
                                Date
                                <k-datepicker v-model="expense.expenseDate"></k-datepicker>
                            </label>
                            <label class="input__label">
                                *Total
                                <k-numeric-input v-model="expense.total" :format="'c2'" :min="0" :class="[{ 'error': hasExpenseTotalError }]" @keydown="hasExpenseTotalError = false"></k-numeric-input>
                                <span class="item-input--vuelidate-error" v-if="hasExpenseTotalError"><i>Total is required</i></span>
                            </label>
                        </div>
                    </section>
                    
                    <section v-if="!editMode" class="upsert-item__section upload-doc">
                        <h2 class="upsert-item__section--title">Upload Document</h2>
                        <div class="dropdown-upload__container">
                            <div class="player-documents__upload-container">
                                <div class="file-detail__row">
                                    <div class="file-detail__item">
                                        <label>
                                            Select File
                                            <k-upload :files="files"
                                                    :multiple="false"
                                                    :batch="false"
                                                    :showActionButtons="false"
                                                    :autoUpload="false"
                                                    @add="onFileAdd"
                                                    @remove="onFileRemove"></k-upload> 
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class="document-info__container">
                                <k-fade :appear="uploadRequest.length > 0">
                                    <p class="field--required">*Required</p>
                                    <div v-for="file in uploadRequest" :key="file.documentPath" class="file-detail__container">
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    *Document Name 
                                                    <k-input v-model="file.documentName" :class="[{ 'error': hasUploadNameError }]" @keydown="hasUploadNameError = false" />
                                                    <span class="item-input--vuelidate-error" v-if="hasUploadNameError"><i>Document name is required</i></span>
                                                </label>
                                            </div>
                                        </div>
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    Document Description
                                                    <k-input v-model="file.documentDescription" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </k-fade>
                            </div>
                        </div>
                    </section>

                    <section v-if="editMode" class="upsert-item__section single upload-documents">
                        <div class="section__content">
                            <div class="dropdown-upload__container">
                                <div class="player-documents__dropdown-container">
                                    <div class="file-detail__row">
                                        <div class="file-detail__item">
                                            <label>
                                                Select Document
                                                <k-dropdown :data-items="filteredDocuments"
                                                            v-model="selectedDocument"
                                                            :text-field="'documentName'"
                                                            :default-item="{ documentName: 'Select Document', documentId: null }"
                                                            :filterable="true"
                                                            @filterchange="onDocumentFilterChange"></k-dropdown>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <p class="document-selection--or">- OR -</p>
                                <div class="player-documents__upload-container">
                                    <div class="file-detail__row">
                                        <div class="file-detail__item">
                                            <label>
                                                Upload New Document
                                                <k-upload :files="files"
                                                        :multiple="false"
                                                        :batch="false"
                                                        :showActionButtons="false"
                                                        :autoUpload="false"
                                                        @add="onFileAdd"
                                                        @remove="onFileRemove"></k-upload>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <k-fade :appear="uploadRequest.length > 0 || selectedDocument?.documentId != null" :class="['upload-detail__animation', { 'active': uploadRequest.length > 0 || selectedDocument?.documentId != null }]">
                                <div v-if="uploadRequest.length > 0">
                                    <p class="field--required">*Required</p>
                                    <div v-for="file in uploadRequest" :key="file.documentPath" class="file-detail__container">
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    *Document Name 
                                                    <k-input v-model="file.documentName" :class="[{ 'error': hasUploadNameError }]" @keydown="hasUploadNameError = false" />
                                                    <span class="item-input--vuelidate-error" v-if="hasUploadNameError"><i>Document name is required</i></span>
                                                </label>
                                            </div>
                                        </div>
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    Document Description
                                                    <k-input v-model="file.documentDescription" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="selectedDocument?.documentId != null">
                                    <div class="file-detail__container">
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    *Document Name 
                                                    <k-input v-model="selectedDocument.documentName" :class="[{ 'error': hasUploadNameError }]" @keydown="hasUploadNameError = false" />
                                                    <span class="item-input--vuelidate-error" v-if="hasUploadNameError"><i>Document name is required</i></span>
                                                </label>
                                            </div>
                                        </div>
                                        <div class="file-detail__row">
                                            <div class="file-detail__item">
                                                <label>
                                                    Document Description
                                                    <k-input v-model="selectedDocument.documentDescription" />
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </k-fade>
                        </div>
                    </section>

                    <k-action-bar>
                        <k-button class="item-save__btn" @click="onSaveClicked">Save</k-button>
                        <k-button class="item-cancel__btn" @click="onCancelClicked">Cancel</k-button>
                    </k-action-bar>
                </div>
            </k-dialog>
        </transition> 

        <div class="loader-container" v-if="isLoading">
            <k-loader :size="'large'" :type="'infinite-spinner'"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>

        <k-notification-group class="default-notification-container">
                <k-fade :appear="successNotification">
                    <k-notification v-if="successNotification"
                                    :type="{ style: 'success', icon: true }"
                                    :closable="true"
                                    @close="onCloseNotification('successNotification')">
                        <span>{{ notificationMsg }}</span>
                    </k-notification>
                </k-fade>
        </k-notification-group>
    </div>
</template>

<script>
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Button } from "@progress/kendo-vue-buttons";
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade } from "@progress/kendo-vue-animation";
    import { DatePicker } from '@progress/kendo-vue-dateinputs';
    import { NumericTextBox, Input } from "@progress/kendo-vue-inputs";
    import { Upload } from '@progress/kendo-vue-upload';
    import { DropDownList } from '@progress/kendo-vue-dropdowns';

    import { filterBy } from '@progress/kendo-data-query';

    import cloneDeep from 'lodash.clonedeep';

    import documentHelpers from '../helpers/documentHelpers.js';

    import auth from '../authConfig';
    import apiService from '../api/api.service.js';

    export default {
        name: 'upsertExpenseDialog',
        props: ['addMode', 'editedExpense', 'editMode', 'playerDocuments', 'playerId', 'showDialog'],
        emits: ['dialogclosed', 'expensesupdated'],
        data() {
            return {
                documentRootFolders: [],
                editDocumentMode: false,
                errorMsg: '',
                errorTitle: '',
                expense: {},
                expenseModel: {
                    total: null,
                    expenseDate: null,
                    description: null,
                    playerId: null
                },
                files: [],
                filteredDocuments: [],
                hasExpenseDescriptionError: false,
                hasExpenseTotalError: false,
                hasUploadNameError: false,
                isLoading: false,
                notificationMsg: '',
                selectedDocument: null,
                showErrorDialog: false,
                successNotification: false,
                uploadDocumentMode: false,
                uploadRequest: []
            }
        },
        computed: {
            defaultFolderName() {
                return this.documentFolderNames.expensesFolderName;
            },
            documentFolderNames() {
                return documentHelpers.documentFolderNames;
            }
        },
        watch: {
            selectedDocument(val) {
                if (val && val.documentId) {
                    this.files = [];
                    this.uploadRequest = [];
                    this.uploadDocumentMode = false;
                    this.editDocumentMode = true;
                }
            },
            async showDialog(val) {
                if (val) {
                    this.assignExpense();

                    if (this.editMode && this.documentRootFolders?.length === 0) {
                        this.isLoading = true;
                        await this.getDocumentRootFolders();
                        this.isLoading = false;
                    }
                } else {
                    this.resetDefaults();
                }
            }
        },
        methods: {
            assignExpense() {
                if (this.editMode) {
                    this.filteredDocuments = this.playerDocuments ? cloneDeep(this.playerDocuments) : [];

                    if (this.editedExpense?.associatedDocumentId) {
                        this.selectedDocument = this.filteredDocuments?.find(doc => doc.documentId === this.editedExpense.associatedDocumentId);
                    }

                    return this.expense = cloneDeep(this.editedExpense);
                }

                this.expense = cloneDeep(this.expenseModel);
            },
            async createExpense(expense) {
                let request = {
                    expense: expense,
                    documents: expense.documents
                };

                let self = this;

                this.isLoading = true;
                this.$emit('dialogclosed');

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.CreateExpense(tokenResponse.accessToken, request)
                    .then((res) => {
                        if (res.data.data) {
                            self.isLoading = false;
                            self.showNotification('Expense created successfully');

                            self.$emit('expensesupdated', res.data.data)
                        }
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            formatUploadRequest() {
                Promise.all(this.files.map(async(file) => {
                    let selectedDocumentFolder = null;

                    let rawFile = file.getRawFile();

                    let fileBase64 = await this.getFileBase64(rawFile);

                    return {
                        base64File: fileBase64.split(',')[1],
                        documentFullName: rawFile.name,
                        documentExtension: file.extension,
                        documentName: rawFile.name.substring(0, rawFile.name.lastIndexOf('.')) || rawFile.name,
                        documentType: rawFile.type,
                        documentPath: `/${rawFile.name}`,
                        documentFolder: this.defaultFolderName,
                        documentDescription: '',
                        folderError: false,
                        selectedDocumentFolder: selectedDocumentFolder
                    };
                })).then(data => {
                    this.uploadRequest = data;
                })
            },
            async getDocumentRootFolders() {
                let self = this;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetDocumentRootFolders(tokenResponse.accessToken)
                    .then((res) => {
                        self.documentRootFolders = res.data.data ?? [];
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });

            },
            getFileBase64(file) {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                });
            }, 
            onCancelClicked() {
                this.$emit('dialogclosed');
            }, 
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onDocumentFilterChange(evt) {
                this.filteredDocuments = filterBy(cloneDeep(this.playerDocuments), evt.filter);
            },
            onFileAdd(evt) {
                if (this.editMode) {
                    this.selectedDocument = { documentName: 'Select Document', documentId: null };
                    this.editDocumentMode = false;
                    this.uploadDocumentMode = true;
                }

                this.files = evt.newState;
                this.formatUploadRequest();
            },
            onFileRemove(evt) {
                this.files = evt.newState;
                this.uploadRequest = [];
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            async onSaveClicked() {
                if (!this.expense.description || !this.expense.total) {
                    if (!this.expense.description) {
                        this.hasExpenseDescriptionError = true;
                    }

                    if (!this.expense.total) {
                        this.hasExpenseTotalError = true;
                    }

                    return;
                }

                if (this.uploadRequest.length == 1 && !this.uploadRequest[0].documentName) {
                    return this.hasUploadNameError = true;
                }

                if (this.editDocumentMode) {
                    if (!this.selectedDocument.documentName) {
                        return this.hasUploadNameError = true;
                    }
                }

                this.expense.playerId = this.playerId;

                if (!this.editMode) {
                    this.uploadRequest.forEach(file => {
                        let fileCode = this.playerId.split('-').join('');
                        file.documentFullName = `${file.documentName}~${fileCode}${file.documentExtension}`;
                        file.documentPath = `${file.documentFolder}/${file.documentName}~${fileCode}${file.documentExtension}`;
                    });

                    this.expense.documents = cloneDeep(this.uploadRequest);

                    return this.createExpense(cloneDeep(this.expense));
                } else {
                    this.isLoading = true;

                    if (this.uploadDocumentMode) {
                        this.uploadRequest.forEach(file => {
                            let fileCode = this.playerId.split('-').join('');
                            file.documentFullName = `${file.documentName}~${fileCode}${file.documentExtension}`;
                            file.documentPath = `${file.documentFolder}/${file.documentName}~${fileCode}${file.documentExtension}`;
                        });

                        await this.uploadExpenseDocument(this.uploadRequest);
                    }

                    if (this.editDocumentMode) {
                        let request = cloneDeep(this.selectedDocument);

                        let fileCodeAndExtension = request.documentPath.split('~')[1];
                        request.documentFullName = `${this.selectedDocument.documentName}~${fileCodeAndExtension}`;
                        request.documentPath = `${this.defaultFolderName}/${this.selectedDocument.documentName}~${fileCodeAndExtension}`;
                        request.documentFolder = this.defaultFolderName;

                        if (request.documentFolder !== this.selectedDocument.documentFolder) {
                            request.isMoved = true;
                            let folder = this.documentRootFolders.find(folder => folder.name === this.defaultFolderName);

                            if (folder) {
                                request.newFolderId = folder.id;
                            }
                        }

                        let document = this.playerDocuments.find(doc => doc.documentId === request.documentId);
                        
                        if (document && document.documentName !== this.selectedDocument.documentName) {
                            request.isRenamed = true;
                        }

                        await this.updateExpenseDocument(request);
                    }

                    this.updateExpense(cloneDeep(this.expense));
                }
            },
            resetDefaults() {
                this.expense = {};
                this.files = [];
                this.uploadRequest = [];
                this.hasUploadNameError = false;
                this.hasExpenseDescriptionError = false;
                this.hasExpenseTotalError = false;

                this.filteredDocuments = [];
                this.selectedDocument = { documentName: 'Select Document', documentId: null };
                this.editDocumentMode = false;
                this.uploadDocumentMode = false;
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;

                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                }, 4000);
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                    this.errorMsg = '';
                }
            },
            async updateExpense(request) {
                let self = this;

                this.$emit('dialogclosed');

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpdateExpense(tokenResponse.accessToken, request)
                    .then(res => {
                        if (res.data.data) {
                            self.isLoading = false;
                            self.showNotification('Expense updated successfully');

                            self.$emit('expensesupdated', res.data.data)
                        }
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            async updateExpenseDocument(request) {
                let self = this;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpdateExpenseDocument(tokenResponse.accessToken, request, this.playerId, this.expense.expenseId)
                    .then(res => {
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            async uploadExpenseDocument(documentsData) {
                let self = this;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UploadExpenseDocument(tokenResponse.accessToken, documentsData, this.playerId, this.expense.expenseId)
                    .then(res => {
                    }).catch(error => {
                        console.log(error);
                        this.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            }
        },
        components: {
            'k-dialog': Dialog,
            'k-action-bar': DialogActionsBar,
            'k-button': Button,
            'k-loader': Loader,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-datepicker': DatePicker,
            'k-input': Input,
            'k-numeric-input': NumericTextBox,
            'k-upload': Upload,
            'k-dropdown': DropDownList
        }
    }
</script>

<style>
    @import '/src/assets/css/defaultUpsertStyles.css';
    @import '/src/assets/css/uploadDocumentsDialog.css';
    @import '/src/assets/css/upsertDocumentDialog.css';
    @import '/src/assets/css/upsertExpenseDialog.css';
</style>